import React from 'react';
import styled, { keyframes } from 'styled-components';
import { colors, shadows } from '../styles/GlobalStyles';
import { rv, containerWidth, mediaQueries } from '../styles/responsive';
import { Button, GlassPanel } from './ui';
import { LayersIcon } from './ui/Icons';

const floatUp = keyframes`
  0% { transform: translateY(30px); opacity: 0; }
  100% { transform: translateY(0); opacity: 1; }
`;

const HeroSection = styled.section`
  min-height: 100vh;
  width: 100%;
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  padding-top: ${rv(32, 16)};
  background: radial-gradient(circle at 50% 0%, #eef2ff 0%, #f5f3ff 35%, #ffffff 70%);
`;

const Nav = styled.header`
  width: 100%;
  max-width: ${containerWidth(1280, 24)};
  margin: 0 auto;
  padding: ${rv(16, 12)} ${rv(24, 20)};
  display: flex;
  justify-content: space-between;
  align-items: center;
  z-index: 50;
`;

const LogoWrapper = styled.a`
  display: flex;
  align-items: center;
  gap: ${rv(10, 8)};
  text-decoration: none;
`;

const LogoIcon = styled.div`
  width: ${rv(32, 28)};
  height: ${rv(32, 28)};
  border-radius: ${rv(8, 6)};
  background-color: ${colors.brand[900]};
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: ${rv(16, 14)};
    height: ${rv(16, 14)};
  }
`;

const LogoText = styled.span`
  font-size: ${rv(20, 16)};
  font-weight: 700;
  letter-spacing: -0.025em;
  color: ${colors.slate[900]};
`;

const NavLinks = styled.nav`
  display: none;
  align-items: center;
  gap: ${rv(32, 24)};

  ${mediaQueries.tabletUp} {
    display: flex;
  }

  a {
    font-size: ${rv(14, 13)};
    font-weight: 500;
    color: ${colors.slate[600]};
    text-decoration: none;
    transition: color 0.2s ease;

    &:hover {
      color: ${colors.slate[900]};
    }
  }
`;

const NavActions = styled.div`
  display: none;
  align-items: center;
  gap: ${rv(16, 12)};

  ${mediaQueries.tabletUp} {
    display: flex;
  }

  a {
    font-size: ${rv(14, 13)};
    font-weight: 500;
    color: ${colors.slate[600]};
    text-decoration: none;
    transition: color 0.2s ease;

    &:hover {
      color: ${colors.slate[900]};
    }
  }
`;

const HeroContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: ${rv(96, 32)} ${rv(16, 16)};
  z-index: 10;
  width: 100%;
  max-width: ${containerWidth(1024, 16)};
  margin: 0 auto;
  height: 100%;
  position: relative;
`;

const HeroTitle = styled.h1`
  font-size: ${rv(72, 32)};
  font-weight: 800;
  letter-spacing: -0.025em;
  color: ${colors.brand[900]};
  line-height: 1.1;
  margin-bottom: ${rv(24, 20)};
  opacity: 0;
  animation: ${floatUp} 1s cubic-bezier(0.16, 1, 0.3, 1) forwards;

  span {
    background: linear-gradient(to right, ${colors.slate[900]}, ${colors.slate[500]});
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
`;

const HeroDescription = styled.p`
  font-size: ${rv(20, 14)};
  color: ${colors.slate[500]};
  margin-bottom: ${rv(40, 32)};
  max-width: ${containerWidth(640, 24)};
  opacity: 0;
  animation: ${floatUp} 1s cubic-bezier(0.16, 1, 0.3, 1) forwards;
  animation-delay: 100ms;
  line-height: 1.75;
`;

const ButtonGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rv(16, 12)};
  margin-bottom: ${rv(64, 40)};
  opacity: 0;
  animation: ${floatUp} 1s cubic-bezier(0.16, 1, 0.3, 1) forwards;
  animation-delay: 200ms;
  width: 100%;
  max-width: 300px;

  ${mediaQueries.tabletUp} {
    flex-direction: row;
    width: auto;
    max-width: none;
  }
`;

const DashboardPreview = styled(GlassPanel)`
  width: 100%;
  max-width: ${containerWidth(1024, 16)};
  border-radius: ${rv(40, 24)} ${rv(40, 24)} 0 0;
  border-bottom: none;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  transform: translateY(${rv(32, 16)});
  opacity: 0;
  animation: ${floatUp} 1s cubic-bezier(0.16, 1, 0.3, 1) forwards;
  animation-delay: 300ms;
  flex: 1;
  min-height: ${rv(400, 280)};

  ${mediaQueries.tabletUp} {
    flex-direction: row;
  }
`;

const Sidebar = styled.div`
  width: 100%;
  height: auto;
  border-bottom: 1px solid rgba(226, 232, 240, 0.6);
  background: rgba(255, 255, 255, 0.4);
  padding: ${rv(24, 16)};
  display: none;
  flex-direction: column;
  gap: ${rv(24, 16)};

  ${mediaQueries.tabletUp} {
    width: ${rv(256, 180)};
    height: 100%;
    border-right: 1px solid rgba(226, 232, 240, 0.6);
    border-bottom: none;
    display: flex;
  }
`;

const SidebarHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${rv(12, 8)};
  padding: ${rv(8, 6)};
  margin-bottom: ${rv(16, 12)};
`;

const SidebarPlaceholder = styled.div<{ $width?: string; $height?: string; $bg?: string }>`
  width: ${({ $width }) => $width || '100%'};
  height: ${({ $height }) => $height || rv(16, 12)};
  border-radius: 4px;
  background: ${({ $bg }) => $bg || colors.slate[200]};
`;

const SidebarNav = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rv(8, 6)};
`;

const SidebarItem = styled.div<{ $active?: boolean }>`
  width: 100%;
  height: ${rv(40, 36)};
  border-radius: ${rv(12, 10)};
  display: flex;
  align-items: center;
  padding: 0 ${rv(16, 12)};
  gap: ${rv(12, 10)};
  background: ${({ $active }) => ($active ? colors.white : 'transparent')};
  box-shadow: ${({ $active }) => ($active ? '0 1px 3px rgba(0,0,0,0.1)' : 'none')};
  border: ${({ $active }) => ($active ? `1px solid ${colors.slate[100]}` : 'none')};
`;

const SidebarDot = styled.div<{ $active?: boolean }>`
  width: ${rv(16, 12)};
  height: ${rv(16, 12)};
  border-radius: 50%;
  background: ${({ $active }) => ($active ? 'rgba(15, 23, 42, 0.2)' : colors.slate[200])};
`;

const MainContent = styled.div`
  flex: 1;
  padding: ${rv(32, 16)};
  background: rgba(255, 255, 255, 0.2);
  display: flex;
  flex-direction: column;
  gap: ${rv(32, 16)};
`;

const ContentHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
`;

const ContentTitle = styled.div``;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${rv(16, 12)};

  ${mediaQueries.tabletUp} {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const StatCard = styled.div`
  background: ${colors.white};
  border-radius: ${rv(16, 12)};
  border: 1px solid ${colors.slate[100]};
  box-shadow: ${shadows.bento};
  padding: ${rv(16, 12)} ${rv(24, 16)};
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const StatDot = styled.div<{ $color: string }>`
  width: ${rv(8, 6)};
  height: ${rv(8, 6)};
  border-radius: 50%;
  background: ${({ $color }) => $color};
`;

const StatLabel = styled.div`
  display: flex;
  align-items: center;
  gap: ${rv(8, 6)};
  margin-bottom: ${rv(12, 8)};
`;

const ChartArea = styled.div`
  flex: 1;
  background: ${colors.white};
  border-radius: ${rv(16, 12)};
  border: 1px solid ${colors.slate[100]};
  box-shadow: ${shadows.bento};
  padding: ${rv(24, 16)};
  display: flex;
  align-items: flex-end;
  gap: ${rv(16, 8)};
  min-height: ${rv(120, 100)};
`;

const ChartBar = styled.div<{ $height: string; $color?: string }>`
  flex: 1;
  height: ${({ $height }) => $height};
  background: ${({ $color }) => $color || colors.slate[100]};
  border-radius: 2px 2px 0 0;
`;

const Header: React.FC = () => {
  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <HeroSection id="header">
      <Nav>
        <LogoWrapper href="/">
          <LogoIcon>
            <LayersIcon size={16} color="white" />
          </LogoIcon>
          <LogoText>뉴럴네트워크</LogoText>
        </LogoWrapper>

        <NavLinks>
          <a href="#services" onClick={(e) => { e.preventDefault(); scrollToSection('services'); }}>Solutions</a>
          <a href="#portfolio" onClick={(e) => { e.preventDefault(); scrollToSection('portfolio'); }}>Portfolio</a>
          <a href="#techstack" onClick={(e) => { e.preventDefault(); scrollToSection('techstack'); }}>Tech Stack</a>
          <a href="#contact" onClick={(e) => { e.preventDefault(); scrollToSection('contact'); }}>About</a>
        </NavLinks>

        <NavActions>
          <Button $variant="primary" onClick={() => scrollToSection('contact')}>Get started</Button>
        </NavActions>
      </Nav>

      <HeroContent>
        <HeroTitle>
          Making AI integration <br />
          <span>accessible and effortless</span>
        </HeroTitle>

        <HeroDescription>
          데이터 아키텍처 설계부터 맞춤형 AI 모델 도입까지 — 복잡한 기술 장벽을 허물고 
          기업의 압도적인 성장을 위한 모든 과정을 완벽하게 지원합니다.
        </HeroDescription>

        <ButtonGroup>
          <Button onClick={() => scrollToSection('contact')}>Get started</Button>
          <Button $variant="secondary" onClick={() => scrollToSection('contact')}>Schedule a call</Button>
        </ButtonGroup>

        <DashboardPreview>
          <Sidebar>
            <SidebarHeader>
              <SidebarPlaceholder $width="1.5rem" $height="1.5rem" />
              <SidebarPlaceholder $width="6rem" $height="1rem" />
            </SidebarHeader>
            <SidebarNav>
              <SidebarItem $active>
                <SidebarDot $active />
                <SidebarPlaceholder $width="5rem" $height="0.75rem" $bg={colors.slate[800]} />
              </SidebarItem>
              <SidebarItem>
                <SidebarDot />
                <SidebarPlaceholder $width="4rem" $height="0.75rem" $bg={colors.slate[400]} />
              </SidebarItem>
              <SidebarItem>
                <SidebarDot />
                <SidebarPlaceholder $width="6rem" $height="0.75rem" $bg={colors.slate[400]} />
              </SidebarItem>
            </SidebarNav>
          </Sidebar>

          <MainContent>
            <ContentHeader>
              <ContentTitle>
                <SidebarPlaceholder $width="8rem" $height="1rem" $bg={colors.slate[300]} style={{ marginBottom: '0.5rem' }} />
                <SidebarPlaceholder $width="16rem" $height="2rem" $bg={colors.slate[800]} />
              </ContentTitle>
            </ContentHeader>

            <StatsGrid>
              <StatCard>
                <StatLabel>
                  <StatDot $color="#4ade80" />
                  <SidebarPlaceholder $width="6rem" $height="0.75rem" $bg={colors.slate[400]} />
                </StatLabel>
                <SidebarPlaceholder $width="100%" $height="2rem" $bg={colors.slate[800]} />
              </StatCard>
              <StatCard>
                <StatLabel>
                  <StatDot $color="#60a5fa" />
                  <SidebarPlaceholder $width="5rem" $height="0.75rem" $bg={colors.slate[400]} />
                </StatLabel>
                <SidebarPlaceholder $width="75%" $height="2rem" $bg={colors.slate[800]} />
              </StatCard>
              <StatCard style={{ display: 'none' }} className="tablet-up">
                <StatLabel>
                  <StatDot $color="#a78bfa" />
                  <SidebarPlaceholder $width="7rem" $height="0.75rem" $bg={colors.slate[400]} />
                </StatLabel>
                <SidebarPlaceholder $width="50%" $height="2rem" $bg={colors.slate[800]} />
              </StatCard>
            </StatsGrid>

            <ChartArea>
              <ChartBar $height="30%" />
              <ChartBar $height="50%" $color={colors.slate[200]} />
              <ChartBar $height="80%" $color={colors.brand[900]} />
              <ChartBar $height="60%" $color={colors.slate[200]} />
              <ChartBar $height="90%" $color={colors.brand[900]} />
              <ChartBar $height="40%" />
            </ChartArea>
          </MainContent>
        </DashboardPreview>
      </HeroContent>
    </HeroSection>
  );
};

export default Header;
